import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BlogPageLayout } from "@/components/landing/BlogPageLayout";
import { SEO } from "@/components/SEO";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import { fetchLivePosts, type CmsPost } from "@/lib/blogCms";

const BASE = "https://sorvaine.com";

const fadeIn = {
  hidden: { opacity: 0, y: 16, filter: "blur(4px)" },
  visible: (i: number) => ({
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { delay: i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

export default function Resources() {
  const [cmsPosts, setCmsPosts] = useState<CmsPost[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchLivePosts()
      .then(setCmsPosts)
      .finally(() => setLoading(false));
  }, []);

  // Exclude the featured post from the grid (it renders separately above).
  const cmsFeatured = cmsPosts.find(p => p.is_featured);
  const allArticles = cmsPosts
    .filter(p => !p.is_featured)
    .map(p => ({
      slug: p.slug,
      title: p.title,
      category: p.category,
      readTime: p.read_time,
      description: p.excerpt,
    }));

  return (
    <BlogPageLayout>
      <SEO
        title="The Sorvaine Blog | Founder Updates & Bid Insights"
        description="Founder updates, industry insights, and practical guides for bid and proposal teams."
        canonical={`${BASE}/blog`}
      />

      {/* Hero */}
      <section className="pt-10 pb-10 md:pt-16 md:pb-16">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="text-center">
            <p className="text-xs tracking-[0.25em] uppercase text-gray-400 font-medium mb-3 md:mb-4">Blog</p>
            <h1 className="text-[30px] md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.08]">
              The Sorvaine Blog
            </h1>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-gray-500 max-w-[600px] mx-auto leading-relaxed">
              Industry insights and practical guides for bid and proposal teams.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CMS featured post */}
      {cmsFeatured && (
        <section className="pb-10 md:pb-16">
          <div className="max-w-[1200px] mx-auto px-6">
            <Link
              to={`/resources/${cmsFeatured.slug}`}
              className="group flex flex-col md:flex-row gap-6 md:gap-8 p-6 md:p-10 rounded-xl border border-border bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-300"
            >
              <div className="flex-1 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
                    <Sparkles className="w-3 h-3" />
                    {cmsFeatured.category}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-gray-400">
                    <Clock className="w-3 h-3" />
                    {cmsFeatured.read_time}
                  </span>
                </div>
                <h2 className="text-xl md:text-3xl font-bold text-gray-900 tracking-tight group-hover:text-primary transition-colors duration-300 leading-tight">
                  {cmsFeatured.title}
                </h2>
                <p className="mt-3 text-gray-500 leading-relaxed max-w-3xl whitespace-pre-line">
                  {cmsFeatured.excerpt}
                </p>
                <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-primary">
                  Read article
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
                </span>
              </div>
              {cmsFeatured.cover_image_url && (
                <div className="hidden md:flex items-center justify-center shrink-0 w-56">
                  <img src={cmsFeatured.cover_image_url} alt={cmsFeatured.title} className="w-full h-auto max-h-56 object-contain rounded-lg" />
                </div>
              )}
            </Link>
          </div>
        </section>
      )}

      {/* Loading skeletons (mirror featured + grid layout) */}
      {loading && (
        <>
          <section className="pb-16">
            <div className="max-w-[1200px] mx-auto px-6">
              <div className="flex flex-col md:flex-row gap-8 p-8 md:p-10 rounded-xl border border-border bg-white shadow-sm">
                <div className="flex-1 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-3 w-20 rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-14 rounded bg-gray-100 animate-pulse" />
                  </div>
                  <div className="h-7 md:h-8 w-3/4 rounded bg-gray-100 animate-pulse" />
                  <div className="h-7 md:h-8 w-1/2 mt-2 rounded bg-gray-100 animate-pulse" />
                  <div className="mt-4 space-y-2">
                    <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-5/6 rounded bg-gray-100 animate-pulse" />
                  </div>
                  <div className="h-4 w-24 mt-5 rounded bg-gray-100 animate-pulse" />
                </div>
                <div className="hidden md:block shrink-0 w-56 h-40 rounded-lg bg-gray-100 animate-pulse" />
              </div>
            </div>
          </section>
          <section className="pb-24">
            <div className="max-w-[1200px] mx-auto px-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col h-full p-6 rounded-lg border border-border bg-white shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
                      <div className="h-3 w-12 rounded bg-gray-100 animate-pulse" />
                    </div>
                    <div className="h-4 w-5/6 rounded bg-gray-100 animate-pulse" />
                    <div className="h-4 w-2/3 mt-2 rounded bg-gray-100 animate-pulse" />
                    <div className="mt-3 space-y-2">
                      <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
                      <div className="h-3 w-4/5 rounded bg-gray-100 animate-pulse" />
                    </div>
                    <div className="h-3 w-20 mt-4 rounded bg-gray-100 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Articles grid — 3 columns */}
      {!loading && allArticles.length > 0 && (
        <section className="pb-24">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {allArticles.map((article, i) => (
                <motion.div key={article.slug} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} variants={fadeIn}>
                  <Link
                    to={`/resources/${article.slug}`}
                    className="group flex flex-col h-full p-6 rounded-lg border border-border bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary/80">
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-[16px] font-semibold text-gray-900 tracking-tight group-hover:text-primary transition-colors duration-300 leading-snug">
                      {article.title}
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1">
                      {article.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-primary/70 group-hover:text-primary transition-colors duration-300">
                      Read article
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 md:p-14 text-center">
            <h2 className="text-xl md:text-3xl font-semibold tracking-tight text-gray-900">
              Want to see Sorvaine in action?
            </h2>
            <p className="mt-3 text-sm md:text-base text-gray-500 max-w-xl mx-auto md:whitespace-nowrap">
              Book a demo and see exactly how Sorvaine turns a tender into a first draft.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/book-demo">
                <button className="inline-flex items-center justify-center px-8 h-11 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97] shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                  Book a Demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </Link>
              <Link to="/rfp-response-software">
                <button className="inline-flex items-center justify-center px-8 h-11 rounded-md border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900 text-sm font-semibold transition-all duration-200 active:scale-[0.97]">
                  See how it works.
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </BlogPageLayout>
  );
}
